import { useTranslations } from "@fenetech/translations";
import { AlertColor } from "@mui/material";
import SaberisAPI from "components/Saberis/SaberisAPI";
import { isNullOrWhiteSpace } from "helpers/objects";
import { IQuote, IQuotePermissions } from "models/IQuote";
import { ISaberisDocumentStatus } from "helpers/interfaces";
import React, { useCallback, useEffect } from 'react';
import useUserInfo from "helpers/context/User/useUserInfo";
import { RoleEnum } from "helpers/enums";
import useMessageBox from "helpers/context/Page/useMessageBox";

interface IProps {
    quote: IQuote | undefined,
    quotePermissions: IQuotePermissions | undefined,
    saberisDocumentStatus: ISaberisDocumentStatus | null,
    redirectPath?: string
    setSaberisDocumentStatus: React.Dispatch<React.SetStateAction<ISaberisDocumentStatus | null>>,
    setAlert: React.Dispatch<React.SetStateAction<[text: string, alertType: AlertColor] | null>>
}

const useSaberisQuoteHelper = ({ quote, quotePermissions, saberisDocumentStatus, redirectPath, setSaberisDocumentStatus, setAlert }: IProps) => {

    const tm = useTranslations();
    const messageBox = useMessageBox();
    const user = useUserInfo();

    const getSaberisDocumentStatus = useCallback(async () => {
        if (quote) {
            const saberisDocumentStatus = await SaberisAPI.GetLatestQuoteDocumentStatus(quote.oKey);
            setSaberisDocumentStatus(saberisDocumentStatus);
        }
    }, [quote, setSaberisDocumentStatus]);

    const launchSaberisLogin = useCallback(async () => {
        if (quote) {
            const urlResponse = await SaberisAPI.GetLoginURL();
            if (!urlResponse.IsOk()) {
                setAlert([tm.Get("Saberis is currently unavailable. Try again later."), "error"]);
            } else if (!isNullOrWhiteSpace(urlResponse.Result.error)) {
                setAlert([tm.GetWithParams("Saberis returned error message: '{0}'. Contact the administrator.", urlResponse.Result.error!), "error"]);
            } else {
                const baseURL = document.getElementsByTagName("base")[0].href;

                // Saberis has an interesting way of getting to its login. We need to POST to a particular URL as if we were submtitting a form.
                // The browser is expected to navigate, so a regular API call won't work.
                // Mimic the creation of the form with the correct information and submit it.
                var form = document.createElement('form');
                var callback = document.createElement('input');
                form.method = "POST";
                form.action = urlResponse.Result.loginPageURL;
                callback.value = `${baseURL}saberis/landingpage?redirectpath=${encodeURIComponent(redirectPath ?? "/dashboard")}`;
                callback.name = "CallbackURL";
                callback.style.display = "none";
                form.appendChild(callback);
                document.body.appendChild(form);
                console.log("test");
                form.submit();
            }
        }
    }, [quote, tm, setAlert, redirectPath]);

    const uploadSaberisDocument = useCallback(async () => {
        if (quote) {
            const uploadDocumentResponse = await SaberisAPI.UploadXml(quote.oKey);
            if (uploadDocumentResponse.IsOk() && uploadDocumentResponse.Result.authRequired) {
                messageBox.Show({ 
                    message: tm.Get("Saberis authentication is required before performing this action. Proceed to the Saberis login portal?"),
                    title: tm.Get("Quote Entry"),
                    yesNoPrompt: true,
                    callback: (dialogResult) => {
                        if (dialogResult){
                            launchSaberisLogin();
                        }
                    }
                });
            } else if (!uploadDocumentResponse.IsOk()) {
                setAlert([tm.Get("Saberis is currently unavailable. Try again later."), "error"]);
            } else if (uploadDocumentResponse.Result.payload.messages?.length > 0) {
                setAlert([tm.GetWithParams("Saberis returned error message: '{0}'. Contact the administrator.", uploadDocumentResponse.Result.payload.messages!.join("; ")), "error"]);
            } else {
                getSaberisDocumentStatus();
            }
        }
    }, [quote, getSaberisDocumentStatus, launchSaberisLogin, setAlert, tm, messageBox]);

    const refreshSaberisDocument = useCallback(async () => {
        if (quote) {
            const updateDocumentStatusResponse = await SaberisAPI.QueryDocumentStatus(saberisDocumentStatus!.documentGuid);
            if (updateDocumentStatusResponse.IsOk() && updateDocumentStatusResponse.Result.authRequired) {
                messageBox.Show({ 
                    message: tm.Get("Saberis authentication is required before performing this action. Proceed to the Saberis login portal?"),
                    title: tm.Get("Quote Entry"),
                    yesNoPrompt: true,
                    callback: (dialogResult) => {
                        if (dialogResult){
                            launchSaberisLogin();
                        }
                    }
                });
            } else if (!updateDocumentStatusResponse.IsOk()) {
                setAlert([tm.Get("Saberis is currently unavailable. Try again later."), "error"]);
            } else if (updateDocumentStatusResponse.Result.payload.messages?.length > 0) {
                setAlert([tm.GetWithParams("Saberis returned error message: '{0}'. Contact the administrator.", updateDocumentStatusResponse.Result.payload.messages!.join("; ")), "error"]);
            } else {
                getSaberisDocumentStatus();
            }
        }
    }, [quote, saberisDocumentStatus, launchSaberisLogin, getSaberisDocumentStatus, setAlert, tm, messageBox]);

    useEffect(() => {
        if (quote && quotePermissions) {
            if (quotePermissions.canAccessSaberis && (user.HasRole(RoleEnum.Dealer) || user.HasRole(RoleEnum.CSR)) && !saberisDocumentStatus) {
                getSaberisDocumentStatus();
            }
        }
    }, [quote, quotePermissions, user, saberisDocumentStatus, getSaberisDocumentStatus]);

    return {
        refreshSaberisDocument,
        uploadSaberisDocument
    }
}

export default useSaberisQuoteHelper;